import {memo} from 'react'
import {View} from 'react-native'
import {msg} from '@lingui/macro'
import {useLingui} from '@lingui/react'

import {useFeedsQuery} from '#/state/queries/word-groups'
import {BlockDrawerGesture} from '#/view/shell/BlockDrawerGesture'
import {useTheme} from '#/alf'
import {atoms as a} from '#/alf'
import {Button} from '#/components/Button'
import {Tabs} from '#/components/ProgressGuide/FollowDialog'
import {Text} from '#/components/Typography'

export function FeedSelector({
  selectedFeedSlug,
  onSelectFeed,
}: {
  selectedFeedSlug: string | null
  onSelectFeed: (slug: string | null) => void
}) {
  const {_} = useLingui()
  const {data: feeds, isLoading} = useFeedsQuery()

  if (isLoading || !feeds || feeds.length === 0) {
    return null
  }

  // Create display names map from feeds
  const displayNames: Record<string, string> = {}
  for (const feed of feeds) {
    displayNames[feed.slug] = feed.name
  }

  // Use feed slugs as keys
  const feedSlugs = feeds.map(feed => feed.slug)

  // Default to first feed if none selected
  const currentSlug = selectedFeedSlug ?? feedSlugs[0]

  return (
    <View style={[a.py_xs]}>
      <BlockDrawerGesture>
        <Tabs
          interests={feedSlugs}
          selectedInterest={currentSlug}
          onSelectTab={slug => {
            onSelectFeed(slug)
          }}
          hasSearchText={false}
          interestsDisplayNames={displayNames}
          TabComponent={Tab}
          contentContainerStyle={[
            {
              paddingLeft: a.px_md.paddingLeft,
            },
          ]}
        />
      </BlockDrawerGesture>
    </View>
  )
}

let Tab = ({
  onSelectTab,
  interest,
  active,
  index,
  interestsDisplayName,
  onLayout,
}: {
  onSelectTab: (index: number) => void
  interest: string
  active: boolean
  index: number
  interestsDisplayName: string
  onLayout: (index: number, x: number, width: number) => void
}): React.ReactNode => {
  const t = useTheme()
  const {_} = useLingui()
  const activeText = active ? _(msg` (active)`) : ''

  return (
    <View
      key={interest}
      onLayout={e =>
        onLayout(index, e.nativeEvent.layout.x, e.nativeEvent.layout.width)
      }>
      <Button
        label={_(msg`Filter by "${interestsDisplayName}"${activeText}`)}
        onPress={() => onSelectTab(index)}>
        {({hovered, pressed, focused}) => (
          <View
            style={[
              a.rounded_full,
              a.px_lg,
              a.py_sm,
              a.border,
              active || hovered || pressed || focused
                ? [t.atoms.bg_contrast_25, t.atoms.border_contrast_medium]
                : [t.atoms.bg, t.atoms.border_contrast_low],
            ]}>
            <Text
              style={[
                active || hovered || pressed || focused
                  ? t.atoms.text
                  : t.atoms.text_contrast_medium,
              ]}>
              {interestsDisplayName}
            </Text>
          </View>
        )}
      </Button>
    </View>
  )
}
Tab = memo(Tab)
